"use client";

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const GetStartedCTASection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section id="get-started" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div
          className={`bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 rounded-3xl p-12 md:p-16 border border-primary/20 transition-all duration-700 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`
          }>

          {/* Icon */}
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <Icon name="SparklesIcon" size={40} className="text-white" />
          </div>

          {/* Content */}
          <h2 className="font-jakarta font-bold text-3xl md:text-5xl text-foreground mb-6 leading-tight tracking-tight">
            Ready to Share Your Story?
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">Join others who trust Legacy to preserve their life stories, personal memories, and heartfelt messages for the people they love most.

          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/old-landing-page#start"
              className="w-full sm:w-auto px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold text-lg hover:bg-primary/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-3 group">

              Start Your Legacy
              <Icon
                name="ArrowRightIcon"
                size={20}
                className="group-hover:translate-x-2 transition-transform" />

            </Link>
            <Link
              href="/old-landing-page#how-it-works"
              className="w-full sm:w-auto px-10 py-4 bg-white text-foreground border-2 border-border rounded-full font-semibold text-lg hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-3">

              <Icon name="PlayCircleIcon" size={24} />
              See How It Works
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-success" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={20} className="text-success" />
              <span>Bank-level security</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="HeartIcon" size={20} className="text-success" />
              <span>People who understand</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default GetStartedCTASection;